import StringMultiDropdownFilter from 'core/elements/grid/filters/StringMultiDropdownFilter'

const anyOpt = { label: 'All', value: 'any' }

const toOptions = (values: (string | undefined)[]) => {
  const unique = [...new Set(values.filter(Boolean))] as string[]
  return [anyOpt, ...unique.map((v) => ({ label: v, value: v }))]
}

// FR-008: empty selection or 'any' selected → pass all rows
const anyOrMatch = (fn: (item: any, value: string[]) => boolean) => (item: any, value: string[]) =>
  !value?.length || value.includes('any') || fn(item, value)

export function buildWorkerNodeFilters(machines: any[], isBYOHCluster: boolean) {
  const filters: any[] = [
    // FR-001: Node Type
    {
      key: 'nodeType',
      label: 'Node Type',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'Node Type' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => [
        anyOpt,
        { label: 'Worker', value: 'Worker' },
        { label: 'Control Plane', value: 'Control Plane' },
        { label: 'GPU', value: 'GPU' },
      ],
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.nodeType)),
    },
    // FR-002: Node Group (dynamic)
    {
      key: 'nodeGroup',
      label: 'Node Group',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'Node Group' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => toOptions(machines.map((m) => m.machineDeployment?.metadata?.name)),
      equalityComparerFn: anyOrMatch((item, value) =>
        value.includes(item.machineDeployment?.metadata?.name),
      ),
    },
    // FR-003: GPU Enabled
    {
      key: 'gpuEnabled',
      label: 'GPU Enabled',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'GPU Enabled' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => [anyOpt, { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }],
      equalityComparerFn: anyOrMatch((item, value) =>
        value.includes(item.gpu?.enabled ? 'yes' : 'no'),
      ),
    },
    // FR-004: Node Status (k8s condition)
    {
      key: 'nodeStatus',
      label: 'Node Status',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'Node Status' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => [
        anyOpt,
        { label: 'Ready', value: 'Ready' },
        { label: 'MemoryPressure', value: 'MemoryPressure' },
        { label: 'DiskPressure', value: 'DiskPressure' },
        { label: 'PIDPressure', value: 'PIDPressure' },
        { label: 'NetworkUnavailable', value: 'NetworkUnavailable' },
        { label: 'NotReady', value: 'NotReady' },
        { label: 'Unknown', value: 'Unknown' },
      ],
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.k8sNodeStatusType)),
    },
    // FR-005: VM Status (machine phase)
    {
      key: 'vmStatus',
      label: 'VM Status',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'VM Status' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => [
        anyOpt,
        { label: 'Running', value: 'Running' },
        { label: 'Provisioning', value: 'Provisioning' },
        { label: 'Provisioned', value: 'Provisioned' },
        { label: 'Failed', value: 'Failed' },
        { label: 'ScalingUp', value: 'ScalingUp' },
        { label: 'ScalingDown', value: 'ScalingDown' },
        { label: 'Deleting', value: 'Deleting' },
        { label: 'Ready', value: 'Ready' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Unknown', value: 'Unknown' },
      ],
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.phase)),
    },
    // FR-007: K8s Version (dynamic)
    {
      key: 'k8sVersion',
      label: 'K8s Version',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'K8s Version' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => toOptions(machines.map((m) => m.spec?.version)),
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.spec?.version)),
    },
  ]

  // FR-006: Flavor hidden for BYOH clusters
  if (!isBYOHCluster) {
    filters.splice(5, 0, {
      key: 'flavor',
      label: 'Flavor',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'Flavor' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => toOptions(machines.map((m) => m.machineFlavor)),
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.machineFlavor)),
    })
  }

  return filters
}
